import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Check, KeyRound, ShieldCheck, ShieldOff, Copy, Loader2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * TOTP 2FA enrollment + management widget for AccountProfile.
 *
 * Three rendered states:
 *   1. NOT_ENABLED + no setup in progress → "Enable two-factor" CTA.
 *   2. NOT_ENABLED + setup in progress (after clicking enable) → QR code +
 *      secret + 6-digit verification input + recovery-codes preview.
 *   3. ENABLED → status pill + "Disable" button that requires the user to
 *      reverify their password AND submit a current OTP.
 *
 * Backups: the recovery codes are shown exactly ONCE — on enrollment.
 * After enrollment the user must regenerate (by disabling + re-enabling)
 * to get fresh codes; this matches Google/GitHub/1Password UX.
 */
export default function TwoFactorAuthCard() {
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null); // {secret, otpauth_uri, recovery_codes}
  const [otp, setOtp] = useState("");
  const [pw, setPw] = useState("");
  const [disableOtp, setDisableOtp] = useState("");
  const [showDisable, setShowDisable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [copiedSecret, setCopiedSecret] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/2fa/status");
      setStatus(data);
    } catch { setStatus({ two_factor_enabled: false, has_pending_enrollment: false, recovery_codes_remaining: 0 }); }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/2fa/status");
        if (mounted) setStatus(data);
      } catch {
        if (mounted) setStatus({ two_factor_enabled: false, has_pending_enrollment: false, recovery_codes_remaining: 0 });
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleSetup = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
      setOtp("");
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start 2FA setup.");
    } finally { setBusy(false); }
  };

  const handleVerify = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/2fa/verify", { code: otp.trim() });
      setSuccess("Two-factor authentication is now active. Keep your recovery codes safe — they are shown above and won't be displayed again.");
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || "Invalid code. Try again.");
    } finally { setBusy(false); }
  };

  const handleDisable = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/2fa/disable", { password: pw, code: disableOtp.trim() });
      setSuccess("Two-factor authentication has been disabled.");
      setShowDisable(false);
      setPw("");
      setDisableOtp("");
      setSetupData(null);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not disable 2FA.");
    } finally { setBusy(false); }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(setupData.secret);
      setCopiedSecret(true);
      window.setTimeout(() => setCopiedSecret(false), 2000);
    } catch { /* clipboard blocked; user can highlight + copy manually */ }
  };

  if (!status) {
    return (
      <section className="border border-[var(--rl-border)] bg-white p-6 mb-6" data-testid="two-factor-card">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold mb-2">
          <KeyRound className="w-4 h-4 text-[var(--rl-brand)]" /> Two-Factor Authentication
        </div>
        <div className="text-xs text-[var(--rl-text-secondary)] flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />Loading…</div>
      </section>
    );
  }

  return (
    <section className="border border-[var(--rl-border)] bg-white p-6 mb-6" data-testid="two-factor-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold">
          <KeyRound className="w-4 h-4 text-[var(--rl-brand)]" /> Two-Factor Authentication
        </div>
        {status.two_factor_enabled ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--rl-success)] text-white text-[10px] uppercase tracking-[0.2em] font-bold" data-testid="two-factor-pill-enabled">
            <ShieldCheck className="w-3 h-3" /> Enabled
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--rl-border)] text-[var(--rl-text)] text-[10px] uppercase tracking-[0.2em] font-bold" data-testid="two-factor-pill-disabled">
            <ShieldOff className="w-3 h-3" /> Off
          </span>
        )}
      </div>

      <p className="text-sm text-[var(--rl-text-secondary)] leading-relaxed">
        Lock your account behind a 6-digit code generated by an app like Google Authenticator, 1Password, or Authy. Strongly recommended for accounts that can launch billable campaigns.
      </p>

      {error && <div className="mt-4 px-3 py-2 bg-[var(--rl-accent)] text-white text-sm font-semibold" data-testid="two-factor-error">{error}</div>}
      {success && <div className="mt-4 px-3 py-2 bg-[var(--rl-success)] text-white text-sm font-semibold" data-testid="two-factor-success">{success}</div>}

      {/* State 1: Not enabled, no enrollment in progress */}
      {!status.two_factor_enabled && !setupData && (
        <button
          type="button" onClick={handleSetup} disabled={busy}
          className="mt-5 inline-flex items-center gap-2 bg-[var(--rl-text)] text-white px-5 py-3 font-semibold hover:bg-[var(--rl-brand)] transition-colors disabled:opacity-60"
          data-testid="two-factor-enable-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
          {busy ? "Generating secret…" : "Enable two-factor authentication"}
        </button>
      )}

      {/* State 2: Enrollment in progress */}
      {setupData && (
        <div className="mt-6 space-y-5" data-testid="two-factor-setup-flow">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2">Step 1 — Scan this QR</div>
            <div className="inline-block p-4 bg-white border-2 border-[var(--rl-border)]" data-testid="two-factor-qr">
              <QRCodeSVG value={setupData.otpauth_uri} size={184} bgColor="#fff" fgColor="#000" />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2">Or paste this secret manually</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-xs bg-[var(--rl-bg-subtle)] p-3 border border-[var(--rl-border)] break-all" data-testid="two-factor-secret">{setupData.secret}</code>
              <button type="button" onClick={copySecret} className="px-3 py-3 border border-[var(--rl-border)] hover:bg-[var(--rl-bg-subtle)]" data-testid="two-factor-copy-secret">
                {copiedSecret ? <Check className="w-4 h-4 text-[var(--rl-success)]" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2">Step 2 — Recovery codes (save them now)</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="two-factor-recovery-codes">
              {setupData.recovery_codes.map((c, i) => (
                <code key={c} className="font-mono text-xs bg-[var(--rl-bg-subtle)] p-2 border border-[var(--rl-border)] text-center" data-testid={`recovery-code-${i}`}>{c}</code>
              ))}
            </div>
            <p className="text-[11px] text-[var(--rl-text-secondary)] mt-2 leading-relaxed">
              Print these or store them in your password manager. Each code works exactly once and we cannot show them again.
            </p>
          </div>
          {!status.two_factor_enabled && (
            <form onSubmit={handleVerify} className="space-y-3">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold">Step 3 — Confirm with a code from your app</div>
              <input
                type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="000000"
                className="w-full bg-white border border-[var(--rl-border)] px-4 py-4 text-3xl font-mono tracking-[0.5em] text-center focus:outline-none focus:border-[var(--rl-brand)]"
                data-testid="two-factor-verify-input"
              />
              <button
                type="submit" disabled={busy || otp.length !== 6}
                className="w-full bg-[var(--rl-text)] text-white py-3 font-semibold hover:bg-[var(--rl-brand)] transition-colors disabled:opacity-50"
                data-testid="two-factor-verify-btn"
              >
                {busy ? "Verifying…" : "Activate two-factor"}
              </button>
            </form>
          )}
        </div>
      )}

      {/* State 3: Enabled — Disable controls */}
      {status.two_factor_enabled && !setupData && (
        <div className="mt-5 space-y-3">
          <div className="text-[11px] text-[var(--rl-text-secondary)]" data-testid="two-factor-recovery-remaining">
            Recovery codes remaining: <span className="font-mono font-bold">{status.recovery_codes_remaining}</span>
          </div>
          {!showDisable ? (
            <button
              type="button" onClick={() => setShowDisable(true)}
              className="inline-flex items-center gap-2 border border-[var(--rl-border)] text-[var(--rl-text)] px-5 py-2.5 font-semibold hover:bg-[var(--rl-bg-subtle)] transition-colors text-sm"
              data-testid="two-factor-show-disable"
            >
              <ShieldOff className="w-4 h-4" /> Disable two-factor
            </button>
          ) : (
            <form onSubmit={handleDisable} className="space-y-3 p-4 border border-[var(--rl-border)] bg-[var(--rl-bg-subtle)]" data-testid="two-factor-disable-form">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold">Confirm: reverify password AND current OTP</div>
              <input
                type="password" required placeholder="Current password" value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="w-full bg-white border border-[var(--rl-border)] px-4 py-3"
                data-testid="two-factor-disable-password"
              />
              <input
                type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required placeholder="6-digit code"
                value={disableOtp}
                onChange={(e) => setDisableOtp(e.target.value.replace(/[^0-9]/g, ""))}
                className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 font-mono tracking-widest text-center"
                data-testid="two-factor-disable-otp"
              />
              <div className="flex items-center gap-2">
                <button
                  type="submit" disabled={busy}
                  className="flex-1 bg-[var(--rl-accent)] text-white py-3 font-semibold disabled:opacity-50"
                  data-testid="two-factor-disable-btn"
                >
                  {busy ? "Disabling…" : "Disable 2FA"}
                </button>
                <button type="button" onClick={() => { setShowDisable(false); setPw(""); setDisableOtp(""); setError(null); }}
                  className="px-4 py-3 border border-[var(--rl-border)] font-semibold">
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </section>
  );
}
